import { gql, useMutation } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router";
import { FormError } from "../components/form-error";
import { createEpisodeMutation, createEpisodeMutationVariables } from "../__generated__/createEpisodeMutation";

interface ICreateEpisodeForm {
  title: string;
  description: string;
  files: File[];
}

const CREATE_EPISODE_MUTATION = gql`
  mutation createEpisodeMutation($createEpisodeInput: CreateEpisodeInput!) {
    createEpisode(input: $createEpisodeInput) {
      ok
      error
    }
  }
`;

interface IParamsType {
  podcastId: string;
}

export const CreateEpisode = () => {
  const [podcastId, setPodcastId] = useState("");
  const [fileError, setFileError] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const params = useParams<IParamsType>();
  const { register, handleSubmit, getValues, formState } = useForm<ICreateEpisodeForm>({
    mode: "onChange",
  });
  useEffect(() => {
    setPodcastId(params.podcastId);
  }, [params.podcastId, podcastId]);

  const onSubmit = async () => {
    const { title, description, files } = getValues();
    if (!loading) {
      if (podcastId) {
        try {
          setFileLoading(true);
          const actualFile = files[0];
          const formBody = new FormData();
          formBody.append("file", actualFile);
          const { url: fileUrl } = await (
            await fetch(
              process.env.NODE_ENV === "production" ? "https://pocast-backend.herokuapp.com/uploads/" : "http://localhost:4000/uploads/",
              {
                method: "POST",
                body: formBody,
              }
            )
          ).json();
          setFileLoading(false);
          createEpisodeMutation({
            variables: {
              createEpisodeInput: {
                podcastId: +podcastId,
                title,
                description,
                fileUrl: fileUrl,
              },
            },
          });
        } catch (e) {
          setFileError("Fail to upload file");
        }
      }
    }
  };
  const history = useHistory();
  const onCompleted = (data: createEpisodeMutation) => {
    const {
      createEpisode: { ok },
    } = data;
    if (ok) {
      history.push(`/manage-podcast/${podcastId}`);
    }
  };
  const [createEpisodeMutation, { loading, data: createEpisodeMutationResult }] = useMutation<
    createEpisodeMutation,
    createEpisodeMutationVariables
  >(CREATE_EPISODE_MUTATION, {
    onCompleted,
  });

  return (
    <div className="h-screen bg-black text-white">
      <Helmet>
        <title>Create Episode | Pocast</title>
      </Helmet>
      <div className="flex flex-col justify-center items-center text-center pt-10">
        <div className="w-full max-w-screen-sm px-5 text-center flex flex-col justify-between ">
          <div className="relative mb-24 mr-auto">
            <div className="text-left text-3xl tracking-wider leading-10 pb-1 text-purple-700">Upload Episode</div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 mt-5  sm:w-96 sm:mx-auto  mb-5 z-10">
            <div className="text-left flex flex-col">
              <label className="mb-2 pl-2 font-bold text-white">Title</label>
              <input
                {...register("title", {
                  required: "Title is required",
                })}
                name="title"
                placeholder="Title"
                autoComplete="off"
                className="rounded-lg p-2 border-2 bg-transparent text-lg bg-gray-900 border-purple-200 transition-colors focus:outline-none focus:border-purple-700 mb-1"
              />
              {formState.errors.title?.message && <FormError errorMessage={formState.errors.title?.message} />}
            </div>
            <div className="text-left flex flex-col">
              <label className="mb-2 pl-2 font-bold text-white">Description</label>
              <textarea
                {...register("description", { required: "Description is required" })}
                name="description"
                placeholder="Description"
                className="rounded-lg focus:outline-none focus:border-purple-700  p-2 border-2 bg-transparent text-lg border-purple-200 transition-colors sm:w-96 sm:mx-auto mb-1 bg-gray-900"
              />
              {formState.errors.description?.message && <FormError errorMessage={formState.errors.description?.message} />}
            </div>
            <div className="text-left flex flex-col">
              <label className="mb-2 pl-2 font-bold text-white">File</label>
              <input type="file" id="file" accept="audio/*" {...register("files", { required: "File is required" })} />
            </div>

            <button
              className={`flex justify-center py-2 rounded-lg font-bold text-white tracking-wider sm:w-96 sm:mx-auto focus:outline-none bg-purple-700 hover:bg-purple-800 transition delay-75 ${
                loading || fileLoading ? "select-none" : ""
              }`}
            >
              {loading || fileLoading ? "Loading ..." : "Upload Episode"}
            </button>
            {createEpisodeMutationResult?.createEpisode.error && (
              <FormError errorMessage={createEpisodeMutationResult?.createEpisode.error} />
            )}
            {fileError && <FormError errorMessage={fileError} />}
          </form>
        </div>
      </div>
    </div>
  );
};
